import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, TextField, Button, Paper, CircularProgress, IconButton, Tooltip, Chip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';

const axiosInst = axios.create({ baseURL: '/api' });
axiosInst.interceptors.request.use(c => {
  const t = localStorage.getItem('dashboard_token');
  if (t) c.headers['Authorization'] = 'Bearer ' + t;
  return c;
});

export default function ChatTab({ userId, onBack }) {
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const prevLenRef = useRef(0);

  const loadChat = useCallback(() => axiosInst.get('/users/' + userId + '/chat')
    .then(r => {
      const sorted = r.data.sort((a,b) => new Date(a.created_at)-new Date(b.created_at));
      setAllMessages(prev => {
        const diff = sorted.length - prev.length;
        if (diff > 0) setNewCount(c => c + diff);
        return sorted;
      });
      setLoading(false);
    }).catch(() => setLoading(false)), [userId]);

  useEffect(() => { loadChat(); const t = setInterval(loadChat, 5000); return () => clearInterval(t); }, [loadChat]);

  useEffect(() => {
    prevLenRef.current = allMessages.length;
  }, [allMessages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setIsAtBottom(atBottom);
    if (atBottom) setNewCount(0);
  };

  const scrollToBottom = () => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); setIsAtBottom(true); setNewCount(0); };

  const grouped = allMessages.reduce((acc, m) => {
    const date = new Date(m.created_at).toLocaleDateString('uk-UA');
    if (!acc[date]) acc[date] = [];
    acc[date].push(m);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort((a,b) => new Date(b.split('.').reverse().join('-')) - new Date(a.split('.').reverse().join('-')));

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axiosInst.post('/users/' + userId + '/chat?message=' + encodeURIComponent(text));
      setText(''); loadChat(); scrollToBottom();
    } catch(e) { alert('Помилка'); }
    setSending(false);
  };

  const deleteDate = async (date) => {
    if (!window.confirm('Видалити за ' + date + '?')) return;
    const iso = date.split('.').reverse().join('-');
    await axiosInst.delete('/users/' + userId + '/chat?date=' + iso);
    setSelectedDate(null); loadChat();
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Видалити?')) return;
    await axiosInst.delete('/users/' + userId + '/chat/message/' + id);
    loadChat();
  };

  const deleteAll = async () => {
    if (!window.confirm('Видалити всю історію?')) return;
    await axiosInst.delete('/users/' + userId + '/chat');
    setAllMessages([]); setSelectedDate(null);
  };

  if (loading) return <CircularProgress sx={{ color:'var(--ac1, #7c4dff)', m:2 }}/>;

  if (!selectedDate) return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', p:1.5 }}>
        {onBack && <IconButton size='small' onClick={onBack} sx={{ color:'var(--ac1, #7c4dff)' }}><ArrowBackIcon/></IconButton>}
        <Typography variant='subtitle2' sx={{ color:'var(--ac1, #7c4dff)', flexGrow:1, ml:1 }}>Дати переписки</Typography>
        {dates.length > 0 && <Tooltip title='Видалити все'><IconButton size='small' onClick={deleteAll} sx={{ color:'#555' }}><DeleteIcon fontSize='small'/></IconButton></Tooltip>}
      </Box>
      {dates.length === 0 ? <Typography sx={{ color:'#555', p:2, textAlign:'center' }}>Немає повідомлень</Typography> :
        <Box sx={{ display:'flex', flexDirection:'column', gap:0.5, px:1.5, bgcolor:'rgba(26,26,46,0.85)', borderRadius:1, py:1 }}>
          {dates.map(date => (
            <Box key={date} sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <Box onClick={() => setSelectedDate(date)} sx={{ flexGrow:1, p:1.5, bgcolor:'rgba(var(--ac1rgb, 124, 77, 255),0.1)', borderRadius:1, cursor:'pointer' }}>
                <Typography variant='body2' sx={{ color:'#fff' }}>📅 {date} ({grouped[date].length} повід.)</Typography>
                <Typography variant='caption' sx={{ color:'#888', display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{grouped[date][grouped[date].length-1]?.message?.substring(0,50)}</Typography>
              </Box>
              <IconButton size='small' onClick={() => deleteDate(date)} sx={{ color:'#555' }}><DeleteIcon fontSize='small'/></IconButton>
            </Box>
          ))}
        </Box>
      }
      <Box sx={{ p:1.5, mt:1, borderTop:'1px solid #333', display:'flex', gap:1 }}>
        <TextField fullWidth size='small' placeholder='Нове повідомлення...'
          value={text} onChange={e => setText(e.target.value)}
          onKeyPress={e => e.key==='Enter' && send()}
          sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
        <Button variant='contained' onClick={send} disabled={sending||!text.trim()} sx={{ bgcolor:'var(--ac1, #7c4dff)', minWidth:42 }}><SendIcon/></Button>
      </Box>
    </Box>
  );

  const msgs = grouped[selectedDate] || [];
  return (
    <Box sx={{ display:'flex', flexDirection:'column', height:480, position:'relative' }}>
      <Box sx={{ display:'flex', alignItems:'center', gap:1, p:1, borderBottom:'1px solid #333' }}>
        <IconButton size='small' onClick={() => setSelectedDate(null)} sx={{ color:'var(--ac1, #7c4dff)' }}><ArrowBackIcon/></IconButton>
        <Typography variant='subtitle2' sx={{ color:'#fff', flexGrow:1 }}>📅 {selectedDate}</Typography>
        <IconButton size='small' onClick={() => deleteDate(selectedDate)} sx={{ color:'#555' }}><DeleteIcon fontSize='small'/></IconButton>
      </Box>
      <Box ref={scrollRef} onScroll={handleScroll} sx={{ flexGrow:1, overflowY:'auto', p:1.5, display:'flex', flexDirection:'column', gap:0.5, bgcolor:'rgba(26,26,46,0.85)', borderRadius:1 }}>
        {msgs.map(m => (
          <Box key={m.id} sx={{ display:'flex', justifyContent: m.direction==='from_admin'?'flex-end':'flex-start', alignItems:'flex-start', gap:0.5 }}>
            {m.direction==='from_user' && <Tooltip title='Видалити'><IconButton size='small' onClick={() => deleteMsg(m.id)} sx={{ color:'#333', mt:0.5 }}><DeleteIcon sx={{ fontSize:12 }}/></IconButton></Tooltip>}
            <Paper sx={{ p:1.5, maxWidth:'75%', bgcolor: m.direction==='from_admin'?'rgba(var(--ac1rgb, 124, 77, 255),0.2)':'rgba(255,255,255,0.05)', border:'1px solid '+(m.direction==='from_admin'?'var(--ac1, #7c4dff)':'#333'), borderRadius:2 }}>
              <Typography variant='body2' sx={{ color:'#fff' }}>{m.message}</Typography>
              <Typography variant='caption' sx={{ color:'#555' }}>{m.direction==='from_admin'?'Адмін':'Юзер'} · {new Date(m.created_at).toLocaleTimeString('uk-UA',{hour:'2-digit',minute:'2-digit'})}</Typography>
            </Paper>
            {m.direction==='from_admin' && <Tooltip title='Видалити'><IconButton size='small' onClick={() => deleteMsg(m.id)} sx={{ color:'#333', mt:0.5 }}><DeleteIcon sx={{ fontSize:12 }}/></IconButton></Tooltip>}
          </Box>
        ))}
        <div ref={bottomRef}/>
      </Box>
      {newCount > 0 && !isAtBottom && (
        <Box sx={{ position:'absolute', bottom:70, left:'50%', transform:'translateX(-50%)' }}>
          <Chip onClick={scrollToBottom} label={'+ ' + newCount + ' нових'} sx={{ bgcolor:'var(--ac1, #7c4dff)', color:'#fff', cursor:'pointer' }}/>
        </Box>
      )}
      <Box sx={{ p:1.5, borderTop:'1px solid #333', display:'flex', gap:1 }}>
        <TextField fullWidth size='small' placeholder='Написати...' value={text} onChange={e => setText(e.target.value)} onKeyPress={e => e.key==='Enter' && send()} sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
        <Button variant='contained' onClick={send} disabled={sending||!text.trim()} sx={{ bgcolor:'var(--ac1, #7c4dff)', minWidth:42 }}><SendIcon/></Button>
      </Box>
    </Box>
  );
}
